import React, { useState, useEffect, useRef } from 'react'
import { Col, Button, Card } from 'antd'
import _ from 'lodash'
import { getSeasonList, getTemplateList, getSummaryReport, downloadSummaryReport } from '../../services/nfcScan/nfcScan'
import { paginationInit, formatMessage, downLoadFileBlob } from '../../utils/common'
import dayjs from 'dayjs'
import { SearchOutlined } from '@ant-design/icons'
import AntdTable from '../../components/table/AntdTable'
import SearchBar from '../../components/searchbar/SearchBar'
import ActionBar from '@/components/table/ActionBar'
import { useSelector } from 'react-redux'
import { modalError } from '../../utils/modalUtils'

const dateFormat = 'YYYY-MM-DD'
const startMonth = dayjs()
  .subtract(1, 'months')
  .add(1, 'day')
const endMonth = dayjs()

const spans = { xl: 8, xxl: 8 }
let seasonLs = []

const NFCScanSummaryReport = () => {
  const commonStore = useSelector(state => state.commonStore)
  const language = useSelector(state => state.commonStore.language)
  const formRef = useRef(null)
  const mounting = useRef(false)

  const columnList = [
    {
      title: formatMessage(commonStore, 'languageObj.nfcScanLocale.fromDate'),
      dataIndex: 'fromDate'
    },
    {
      title: formatMessage(commonStore, 'languageObj.nfcScanLocale.endDate'),
      dataIndex: 'toDate'
    },
    {
      title: formatMessage(commonStore, 'languageObj.nfcScanLocale.season'),
      dataIndex: 'season'
    },
    {
      title: formatMessage(commonStore, 'languageObj.nfcScanLocale.productCode'),
      dataIndex: 'productCode'
    },
    {
      title: formatMessage(commonStore, 'languageObj.nfcScanLocale.productName'),
      dataIndex: 'productName'
    },
    {
      title: formatMessage(commonStore, 'languageObj.nfcScanLocale.totalInventory'),
      dataIndex: 'totalInventory'
    },
    {
      title: formatMessage(commonStore, 'languageObj.nfcScanLocale.totalSellThrough'),
      dataIndex: 'totalSellThrough',
      render: text => textFormat(text)
    },
    {
      title: formatMessage(commonStore, 'languageObj.nfcScanLocale.totalDisplayUnits'),
      dataIndex: 'totalDisplayUnits'
    },
    {
      title: formatMessage(commonStore, 'languageObj.nfcScanLocale.totalTaggedUnits'),
      dataIndex: 'totalTaggedUnits'
    },
    {
      title: formatMessage(commonStore, 'languageObj.nfcScanLocale.totalSalesUnits'),
      dataIndex: 'totalSalesUnits'
    },
    {
      title: formatMessage(commonStore, 'languageObj.nfcScanLocale.periodSalesUnits'),
      dataIndex: 'periodSalesUnits'
    },
    {
      title: formatMessage(commonStore, 'languageObj.nfcScanLocale.scansNo'),
      dataIndex: 'noOfScans'
    },
    {
      title: formatMessage(commonStore, 'languageObj.nfcScanLocale.clicksNo'),
      dataIndex: 'noOfClicks'
    },
    {
      title: formatMessage(commonStore, 'languageObj.nfcScanLocale.scanUsersNo'),
      dataIndex: 'noOfScanUsers'
    },
    {
      title: formatMessage(commonStore, 'languageObj.nfcScanLocale.scanRate'),
      dataIndex: 'scanRate',
      render: text => textFormat(text)
    },
    {
      title: formatMessage(commonStore, 'languageObj.nfcScanLocale.clickRate'),
      dataIndex: 'clickRate',
      render: text => textFormat(text)
    },
    {
      title: formatMessage(commonStore, 'languageObj.nfcScanLocale.scanUserRate'),
      dataIndex: 'scanUserRate',
      render: text => textFormat(text)
    }
  ]

  const searchListInit = [
    {
      type: 'timeRange',
      label: 'languageObj.nfcScanLocale.scanDate',
      key: 'scanDate',
      disabledDate: current => {
        return (
          (current && current > dayjs().endOf('day')) ||
          current <
            dayjs()
              .subtract(6, 'months')
              .startOf('day')
        )
      },
      format: dateFormat,
      initialValues: [startMonth, endMonth],
      spans
    },
    {
      type: 'textarea',
      label: 'languageObj.nfcScanLocale.productCode',
      key: 'productCodes',
      initialValues: undefined,
      spans
    },
    {
      type: 'input',
      label: 'languageObj.nfcScanLocale.productName',
      key: 'productName',
      initialValues: '',
      spans
    },
    {
      type: 'select',
      label: 'languageObj.nfcScanLocale.template',
      key: 'templates',
      initialValues: [],
      mode: 'multiple',
      maxTagCount: 1,
      selectList: [],
      spans
    },
    {
      type: 'select',
      label: 'languageObj.nfcScanLocale.season',
      key: 'seasons',
      initialValues: [],
      mode: 'multiple',
      maxTagCount: 1,
      selectList: [],
      spans
    }
  ]

  const [tableList, setTableList] = useState([])
  const [searchList, setSearchList] = useState(searchListInit)
  const [pagination, setPagination] = useState(paginationInit)
  const [queryFlag, setQueryFlag] = useState(false)

  useEffect(() => {
    initSearchList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (mounting.current) searchDataFun()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryFlag])

  const textFormat = text => {
    return text && (text * 100).toFixed(2) + '%'
  }

  const initSearchList = () => {
    Promise.all([getTemplateList(), getSeasonList()]).then(res => {
      try {
        searchListInit.forEach(searchItem => {
          if (['templates', 'seasons'].includes(searchItem.key)) {
            let options = []
            switch (searchItem.key) {
              case 'templates':
                const templateList = res[0].resultData
                options = templateList.map(item => ({ label: item, value: item }))
                break
              case 'seasons':
                const seasonList = res[1].resultData
                _.forIn(seasonList, (value, key) => {
                  options.push({ label: key, value: key })
                  seasonLs.push(key)
                  if (value) searchItem.initialValues.push(key)
                })
                break
              default:
                break
            }
            searchItem.selectList = options
          }
        })
        setSearchList([...searchListInit])
      } catch (error) {}
    })
  }

  const searchDataBtn = () => {
    mounting.current = true
    const pg = {
      ...pagination,
      currentPage: 1
    }
    setPagination(pg)
    setQueryFlag(!queryFlag)
  }

  const searchClearDataBtn = () => {
    formRef && formRef.current.clearForm()
    const pg = {
      ...pagination,
      currentPage: 1
    }
    setPagination(pg)
    setQueryFlag(!queryFlag)
  }

  const handleTableChange = pagina => {
    const params = {
      ...pagination,
      currentPage: pagina.current,
      pageSize: pagina.pageSize
    }
    setPagination({ ...params })
    setQueryFlag(!queryFlag)
  }

  const getparams = () => {
    const info = (formRef.current && formRef.current.getValue()) || {}
    const { scanDate, productCodes, productName, templates, seasons } = info
    let scanStartDate = scanDate && scanDate[0] ? scanDate[0].format(dateFormat) : null
    let scanEndDate = scanDate && scanDate[1] ? scanDate[1].format(dateFormat) : null
    const params = {
      isChinese: language === 'zh_CN' ? true : false,
      startDate: scanStartDate ? scanStartDate + ' 00:00:00' : scanStartDate,
      endDate: scanEndDate ? scanEndDate + ' 23:59:59' : scanEndDate,
      productCodes: productCodes ? productCodes.split(',') : [],
      productName,
      templates,
      seasons: seasons.length === 0 ? seasonLs : seasons
    }
    return { params, scanStartDate, scanEndDate }
  }

  const searchDataFun = () => {
    const { params, scanStartDate, scanEndDate } = getparams()
    params.size = pagination.pageSize
    params.pageNo = pagination.currentPage
    getSummaryReport(params)
      .then(res => {
        console.log(res)
        const dt = res.resultData
        setTableList(dt)
        const pg = {
          ...pagination,
          total: res.recordsCount
        }
        setPagination(pg)
      })
      .catch(error => {
        console.log(error)
        if (error.errorCode === '508') {
          const dateArea = scanStartDate + ' - ' + scanEndDate
          modalError({
            content: `${formatMessage(commonStore, 'languageObj.nfcScanLocale.summaryReportPageError')} ${dateArea}!`
          })
        }
      })
  }

  const downLoadFun = () => {
    const { params } = getparams()
    downloadSummaryReport(params).then(res => {
      downLoadFileBlob(res)
    })
  }

  return (
    <div>
      <Card bordered={false} bodyStyle={{ padding: 4 }}>
        <div className="search-bar">
          <SearchBar ref={formRef} searchList={searchList}>
            <Col
              xs={24}
              sm={12}
              md={12}
              lg={8}
              xl={8}
              xxl={8}
              className="right_search"
              style={{ justifyContent: 'flex-end' }}
            >
              <Button icon={<SearchOutlined />} type="primary" onClick={searchDataBtn}>
                {formatMessage(commonStore, 'languageObj.commonLocale.search')}
              </Button>
              <Button onClick={searchClearDataBtn}>
                {formatMessage(commonStore, 'languageObj.commonLocale.clear')}
              </Button>
            </Col>
          </SearchBar>
        </div>
        <div className="content-container">
          <ActionBar onDownload={downLoadFun} style={{ alignItems: 'end', marginTop: 15 }} />
          {/* <div className="opt_btn mt15 mb15" style={{ display: 'flex' }}>
            <Button type="primary" onClick={downLoadFun} style={{ marginLeft: '20px' }}>
              {formatMessage(commonStore, 'languageObj.commonLocale.download')}
            </Button>
          </div> */}
          <AntdTable
            rowKey={record => record.tagUid}
            dataSource={tableList}
            columns={columnList}
            // 独一无二的标识，不能重复
            columnsKey="nfcScanSummaryReportColumns"
            pagination={pagination}
            onChange={handleTableChange}
          />
        </div>
      </Card>
    </div>
  )
}
export default NFCScanSummaryReport
